import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import Close from '@mui/icons-material/Close';
import { Button, IconButton, Tooltip } from '@mui/material';
import { InfoWindowF } from '@react-google-maps/api';
import { ISoftwareIntegratorDto, TSoftwareSpecialization } from '@softwareIntegrator/types/softwareIntegrator';
import { getSpecializationFromSlug } from '@softwareIntegrator/utils/softwareIntegrators';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import './index.css';

interface IMapPopupProps {
    integrators: ISoftwareIntegratorDto[];
    onCloseClick: () => void;
    onButtonClick: (integrator: ISoftwareIntegratorDto) => void;
}

interface IPopupIconTooltipProps {
    slug: TSoftwareSpecialization;
}

const PopupIconTooltip = ({ slug }: IPopupIconTooltipProps) => {
    const { icon: Icon, title } = getSpecializationFromSlug(slug);
    return (
        <Tooltip title={title} placement="right" arrow>
            <div className="w-[24px] aspect-square">
                <Icon />
            </div>
        </Tooltip>
    );
};

export const MapPopup = ({ integrators, onCloseClick, onButtonClick }: IMapPopupProps) => {
    const [selectedIntegrator, setSelectedIntegrator] = useState<ISoftwareIntegratorDto | null>(
        integrators.length > 1 ? null : integrators[0]
    );

    const showBackButton = useMemo(
        () => integrators.length > 1 && Boolean(selectedIntegrator),
        [integrators.length, selectedIntegrator]
    );

    const handleBackClick = useCallback(() => setSelectedIntegrator(null), []);

    const handleButtonClickFactory = useCallback(
        (integrator: ISoftwareIntegratorDto) => () => {
            onButtonClick(integrator);
            setSelectedIntegrator(null);
            onCloseClick();
        },
        [onButtonClick, onCloseClick]
    );

    const handleSelectIntegratorFactory = useCallback(
        (integrator: ISoftwareIntegratorDto) => () => setSelectedIntegrator(integrator),
        []
    );

    return (
        <InfoWindowF
            position={{ lat: integrators[0].latitude, lng: integrators[0].longitude }}
            onCloseClick={onCloseClick}
            options={{ headerDisabled: true, minWidth: 310 }}
        >
            <div className="p-1 pb-4 max-h-96 overflow-y-auto">
                <div
                    className={clsx('flex items-start justify-end', {
                        'justify-between': showBackButton,
                    })}
                >
                    {showBackButton && (
                        <Button variant="text" onClick={handleBackClick}>
                            <ArrowBack className="mr-2" />
                            <span>
                                <FormattedMessage id="common.goBack" defaultMessage="Go back" />
                            </span>
                        </Button>
                    )}
                    <IconButton onClick={onCloseClick}>
                        <Close />
                    </IconButton>
                </div>
                <div className="px-3 mt-2">
                    <div className="flex flex-col">
                        {selectedIntegrator ? (
                            <>
                                <p className="font-light font-museo text-xl">
                                    {selectedIntegrator.name === selectedIntegrator.contact_name
                                        ? `${selectedIntegrator.name}`
                                        : `${selectedIntegrator.name} - ${selectedIntegrator.contact_name}`}
                                </p>
                                <p className="font-light font-museo text-base text-black-truffle">{`${
                                    selectedIntegrator.city && `${selectedIntegrator.city}, `
                                }${selectedIntegrator.country.name}`}</p>
                                <div className="flex flex-wrap max-w-80 gap-4 mt-2">
                                    {selectedIntegrator.software_specializations.map((slug) => (
                                        <PopupIconTooltip slug={slug} key={slug} />
                                    ))}
                                </div>

                                <Button
                                    onClick={handleButtonClickFactory(selectedIntegrator)}
                                    className="rounded-full w-full mt-6"
                                >
                                    <FormattedMessage id="common.seeDetails" defaultMessage="See details" />
                                </Button>
                            </>
                        ) : (
                            integrators.map((integrator) => (
                                <Button
                                    variant="text"
                                    className="flex justify-between items-center gap-4 sm:gap-12"
                                    key={integrator.slug}
                                    component={'div'}
                                    onClick={handleSelectIntegratorFactory(integrator)}
                                >
                                    <div>
                                        <p className="font-light font-museo text-xl">
                                            {integrator.name === integrator.contact_name
                                                ? `${integrator.name}`
                                                : `${integrator.name} - ${integrator.contact_name}`}
                                        </p>
                                        <p className="font-light font-museo text-base mt-1 text-black-truffle">{`${
                                            integrator.city && `${integrator.city}, `
                                        }${integrator.country.name}`}</p>
                                    </div>
                                    <div className="w-fit">
                                        <ArrowRightAlt className="w-8" />
                                    </div>
                                </Button>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </InfoWindowF>
    );
};
